import React, { useContext, useState } from 'react';
import { useToast } from '../../../../../providers/ToastProvider';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { translate } from '../../../../../types/translation/Translator';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';
import { AddButton } from '../../../../components/Button';
import { ProductMasterData } from '../../../../../types/product_master_data/productMasterData';
import Table from '../../../../components/Table';
import DeleteIconButton from '../../../../icons/templates/DeleteIconButton';
import CreateProductMasterDataLabelModal from '../Modals/CreateProductMasterDataLabelModal';
import {
  ProductMasterDataMutations,
  UpdateProductMasterDataResponse,
  UpdateProductMasterDataVariables,
} from '../../../../../graphql/productMasterData.graphql';
import useMutation from '../../../../../hooks/useMutation';
import { ProductMasterDataInput } from '../../../../../types/product_master_data';
import { ToastType } from '../../../../components/Toast';

interface ProductMasterDataLabelsPaneProps {
  pmdId: string;
  footer: (input: ProductMasterData) => React.JSX.Element;
}

export default function ProductMasterDataLabelsPane({ pmdId, footer }: ProductMasterDataLabelsPaneProps) {
  const { showToast } = useToast();
  const { productMasterData, setProductMasterData } = useContext(ProductMasterDataContext);

  if (!productMasterData.get(pmdId)) return null;

  const [input, setInput] = useState<ProductMasterDataInput>(new ProductMasterDataInput(productMasterData.get(pmdId)));
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [update, { loading }] = useMutation<UpdateProductMasterDataResponse, UpdateProductMasterDataVariables>(
    ProductMasterDataMutations.update,
    {
      onCompleted: data => {
        const updated = data.updateProductMasterData[0];
        setProductMasterData(new Map(productMasterData).set(updated.id, new ProductMasterData(updated)));
        setInput(new ProductMasterDataInput(updated));
        showToast({
          title: translate({ key: TranslationKey.labelRemovedSuccessfullyToastTitle }),
          type: ToastType.success,
        });
      },
    },
  );

  const handleDelete = (item: string) => {
    input.labels = input.labels.filter(i => i !== item);
    update({ variables: { productMasterData: [input.forUpdate()] } });
  };

  const headers = [
    {
      key: 'identifier',
      name: translate({ key: TranslationKey.identifier }),
      text: (item: string) => item,
    },
    {
      key: 'action',
      name: translate({ key: TranslationKey.actions }),
      text: (item: string) => <DeleteIconButton onClick={() => handleDelete(item)} />,
    },
  ];

  return (
    <>
      {createModalOpen && (
        <CreateProductMasterDataLabelModal
          open={createModalOpen}
          setOpen={setCreateModalOpen}
          input={input}
          setInput={setInput}
        />
      )}

      <div className={`${pmdId ? '' : 'content-x'} flex h-full flex-grow flex-col gap-4`}>
        <div className='grid gap-2'>
          <p className='page-title'>{translate({ key: TranslationKey.labelScreenTitle })}</p>
          <p className='subtitle'>{translate({ key: TranslationKey.labelScreenSubtitle })}</p>
        </div>
        <div className='flex justify-between gap-4'>
          <div></div>
          <div className='flex'>
            <AddButton text={translate({ key: TranslationKey.labelAdd })} onClick={() => setCreateModalOpen(true)} />
          </div>
        </div>
        <Table headers={headers} items={input.labels || []} />
      </div>
      <div>{footer(input)}</div>
    </>
  );
}
